import * as React from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import getMultiFilters from 'modules/core/components/MultiFilters/hooks/utils/getMultiFilters'
import { useEffect } from 'modules/core/hooks/useEffect'

import {
  formatCustomFilters,
  getCustomFilters,
} from 'modules/shift_management/components/ShiftManagement/Content/_filters/customFilters'
import useApplyFilters from 'modules/shift_management/hooks/useApplyFilters'
import {
  useLoadAllEmployees,
  useLoadColumnEmployees,
} from 'modules/shift_management/hooks/useFetchEmployees'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import type { ShiftsRow } from 'modules/shift_management/lib/types'

import type { Column, ColumnsType } from '../utils/buildColumns'
import HeaderCell from './HeaderCell'

interface ResultRefs {
  [key: number]: HTMLDivElement | null
}

interface HeaderProps {
  columns: ColumnsType
  shiftsRows: ShiftsRow[]
}

const Header = ({ columns, shiftsRows }: HeaderProps) => {
  const [{ view }] = useSchedule()
  const { columnDaysSelected, allTableSelected } = useSelectionState()
  const [{ hasMore }] = usePagination()

  const { loadColumnEmployees } = useLoadColumnEmployees()
  const { loadAllEmployees } = useLoadAllEmployees()

  const { filters: multiFilters, logicOperator } = getMultiFilters(
    LOCAL_STORAGE_KEYS.shiftEmployees
  )
  const { getFilteredEmployees } = useApplyFilters(shiftsRows)

  const onSelect = (index: number, day: moment.Moment) => {
    const employeeIds = getFilteredEmployees().map((e) => e.id)
    const custom_filters = getCustomFilters(multiFilters)
    // FIXME: This is unused now? Can we use filters from useShiftManagement
    const filters = {
      employee_ids: employeeIds,
      logic_operator: logicOperator.get('logic_operator').value,
      custom_filters: formatCustomFilters(custom_filters),
    }

    if (index === 0) {
      loadAllEmployees(hasMore, allTableSelected, shiftsRows)
    } else {
      loadColumnEmployees(hasMore, shiftsRows, day)
    }
  }

  const checked = (index: number, dayId: string) =>
    index === 0 ? allTableSelected : columnDaysSelected.includes(dayId)

  const canManageShifts = shiftsRows.some((row) => {
    return row.canManage
  })

  const getFirstShiftIndex = (): number | null => {
    const startingHours = shiftsRows.reduce((result: number[], row) => {
      const hour = row.startingHour(view.firstDay)
      if (hour != null) result.push(hour) // != null to consider shifts starting at 0
      return result
    }, [])

    return startingHours.length ? Math.min(...startingHours) : null
  }

  const dateRefs = React.useRef<ResultRefs>({})
  const firstShiftIndex = getFirstShiftIndex()

  useEffect(() => {
    const item = dateRefs.current[0]
    if (!item || !view.isDaily) return

    // The first column we want to see after the employees one (by default, 6h)
    // ?? to consider shifts starting at 0
    const scrollTo = firstShiftIndex ?? 6
    const employeesWidth = 240 // px
    const columnMinWidth = item.offsetWidth

    // The columns behind the employees column
    const hiddenColumns = Math.ceil(employeesWidth / columnMinWidth)

    // Do scroll to the relative column to see the selected column after the employees one
    const index = scrollTo - hiddenColumns
    dateRefs.current[index]?.scrollIntoView({ block: 'end', inline: 'start' })

    // Disabling the exhaustive deps rule because we want to run this hook only once instead of re-running it when firstShiftIndex changes
  }, [view.isDaily]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {columns.map((heading: Column, index: number) => (
        <HeaderCell
          key={index}
          heading={heading}
          ref={(node: HTMLDivElement) => (dateRefs.current[heading.hour] = node)}
          index={index}
          onSelect={onSelect}
          checked={checked}
          view={view}
          canManageShifts={canManageShifts}
        />
      ))}
    </>
  )
}

export default observer(Header)
